import Title from './Title'
import classNames from 'classnames'
import { Swiper, SwiperSlide, useSwiper } from 'swiper/react'
import { type Swiper as SwiperRef, Navigation, Autoplay } from 'swiper'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import 'swiper/css'
import 'swiper/css/effect-fade'
import { CDNImage } from '~/components/business/CDNImage'
import { MyContainer } from '~/components/ui/MyContainer'
import { AutoScroll } from '~/components/ui/AutoScroll'
import styles from './ModelResult.module.css'
import { ArrowLongLeft, ArrowLongRight, InfoCircle } from 'wujieai-react-icon'
import { MyImage } from '~/components/ui/MyImage'
import { getMirrorTemplateList_mirrorMarketQuery_homeResourcePackage_categoryList } from '~/graphqls/types'
import QRCode from 'qrcode.react'
import { unionBy } from 'lodash'

function ModelResult({
  categoryList,
}: {
  categoryList: getMirrorTemplateList_mirrorMarketQuery_homeResourcePackage_categoryList[]
}) {
  const [activeTab, setActiveTab] = useState(categoryList?.[0]?.id)
  const handleChangeTab = (id?: number | null) => {
    setActiveTab(id)
    stateRef.current.activeTab = id
  }
  const swiperRef = useRef<SwiperRef>()
  const stateRef = useRef<any>({
    activeTab: categoryList?.[0]?.id,
  })

  const selectTemplateList = useMemo(() => {
    // 双人场景下根据模版名称过滤
    if (categoryList.filter(it => it.id === activeTab)?.[0]?.name === '双人') {
      return unionBy(
        categoryList.filter(it => it.id === activeTab)?.[0]?.itemList || [],
        'name'
      )
    }
    return categoryList.filter(it => it.id === activeTab)?.[0]?.itemList || []
  }, [activeTab])

  useEffect(() => {
    // 切换分类slide回到第一个
    swiperRef.current?.slideToLoop(2, 0)
  }, [activeTab])

  useEffect(() => {
    tabAutoPlay()
    return () => {
      clearInterval(stateRef.current.timer)
    }
  }, [])

  const tabAutoPlay = () => {
    // 自动播放
    stateRef.current.timer = setInterval(() => {
      if (!swiperRef.current) {
        return
      }
      const activeTab = stateRef.current.activeTab
      const activeTabIndex = categoryList.findIndex(
        item => item.id === activeTab
      )
      const nextTab = categoryList[activeTabIndex + 1] || categoryList[0]
      handleChangeTab(nextTab.id)
      swiperRef.current.slideToLoop(2, 0)
    }, 18 * 1000)
  }

  return (
    <section className="w-full !min-w-[1424px]  pt-[64px] -pb-[64px] bg-neutral-700">
      <MyContainer
        className={classNames(
          '!w-[1424px] !p-0 flex flex-col items-center',
          styles.container
        )}
      >
        <Title text="模板效果" desc="Template Effects" />

        <div
          className={classNames('w-full h-full mx-auto', styles.swiper)}
          onMouseEnter={() => {
            clearTimeout(stateRef.current.timer)
          }}
          onMouseLeave={() => {
            tabAutoPlay()
          }}
        >
          <Swiper
            modules={[Navigation, Autoplay]}
            slidesPerView={5}
            spaceBetween={24}
            centeredSlides={true}
            loop
            allowTouchMove={false}
            speed={400}
            slideToClickedSlide={true}
            autoplay={{
              delay: 3000,
              pauseOnMouseEnter: true,
              disableOnInteraction: false,
              waitForTransition: true,
            }}
            longSwipes={false}
            onSwiper={swiper => {
              swiperRef.current = swiper
            }}
          >
            <div slot="container-start" className="flex justify-center">
              <SyncTabToSwiper
                handleChange={handleChangeTab}
                activeTab={activeTab}
                categoryList={categoryList}
              />
            </div>

            {selectTemplateList.map((it, i) => {
              return (
                <SwiperSlide key={it.id}>
                  {({ isActive }) => {
                    return (
                      <SwiperItemContent
                        isActive={isActive}
                        it={it}
                        activeTab={activeTab}
                      />
                    )
                  }}
                </SwiperSlide>
              )
            })}
          </Swiper>
          <SlidePrevButton
            onPrev={() => {
              swiperRef.current?.slidePrev()
            }}
          />
          <SlideNextButton
            onNext={() => {
              swiperRef.current?.slideNext()
            }}
          />
          <div className="absolute w-full bottom-[64px] left-0 flex items-center text-neutral-500 text-xs font-semibold justify-center">
            <InfoCircle className="mr-2" size={16} />
            提供300+通用模板，并支持模板定制化，以上为部分模板展示
          </div>
        </div>
      </MyContainer>
    </section>
  )
}
const SwiperItemContent = ({
  isActive,
  it,
  activeTab,
}: {
  isActive: boolean
  it: any
  activeTab: any
}) => {
  const [isHover, setIsHover] = useState(false)
  return (
    <div
      className={classNames(styles.slideCard, {
        [styles.active]: isActive,
      })}
      onClick={e => e.stopPropagation()}
      onMouseEnter={() => {
        if (isActive) {
          setIsHover(true)
        }
      }}
      onMouseLeave={() => {
        if (isActive) {
          setIsHover(false)
        }
      }}
    >
      <div
        className={classNames(styles.modelContainer, {
          [styles.hoverItem]: isHover,
        })}
      >
        <div className="w-[213px] h-[320px] relative">
          <MyImage
            tag="v800"
            className="w-full h-full rounded-[12px] object-cover"
            src={it.image}
          />
          {isActive && (
            <div className={styles.hoverContainer}>
              <div className="w-full h-full absolute left-0 top-0 rounded-[12px] backdrop-blur-sm bg-neutral-50 bg-opacity-[0.16] flex items-center justify-center">
                <div className="bg-neutral-900 rounded-[8px] p-2">
                  <QRCode
                    value={`${process.env.NEXT_PUBLIC_MIRROR_MOBILE_HOST}/weapp?name=scanCode&templateId=${it.id}`}
                    size={120}
                    fgColor="#000"
                    imageSettings={{
                      src: '/images/mirror/wujie-logo.jpg',
                      height: 24,
                      width: 24,
                      excavate: true,
                    }}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={classNames(
            'flex flex-1 justify-center items-center mt-3',
            {
              [styles.hiddenModelName]: isActive,
            }
          )}
        >
          <div className={classNames(styles.modelName)}>{it.name}</div>

          {isActive && (
            <div
              className="flex justify-center items-center rounded-full px-2 py-1 text-xs font-semibold cursor-pointer text-[#BF8BE5] ml-2 whitespace-nowrap"
              style={{
                background: 'rgba(191, 139, 229, 0.14)',
              }}
            >
              <CDNImage
                className="mr-[2px]"
                src="/images/mirror/magic-icon.svg"
              />
              试用
            </div>
          )}
        </div>
        {isActive && (
          <div className={classNames(styles.qrcodeText)}>
            <div className={classNames(styles.modelName)}>
              扫描二维码即可试用
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
const SlideNextButton = ({ onNext }: { onNext: () => void }) => {
  return (
    <div
      className={classNames('-right-[68px]', styles.navigationBtn)}
      onClick={onNext}
    >
      <ArrowLongRight />
    </div>
  )
}

const SlidePrevButton = ({ onPrev }: { onPrev: () => void }) => {
  return (
    <div
      className={classNames('-left-[68px]', styles.navigationBtn)}
      onClick={onPrev}
    >
      <ArrowLongLeft />
    </div>
  )
}

const SyncTabToSwiper: React.FC<{
  categoryList: getMirrorTemplateList_mirrorMarketQuery_homeResourcePackage_categoryList[]
  handleChange: (id?: number | null) => void
  activeTab?: number | null
}> = ({ categoryList, activeTab, handleChange }) => {
  return (
    <AutoScroll
      activeIndex={categoryList.findIndex(it => it.id === activeTab)}
      gutter={8}
      className="max-w-[1280px]"
    >
      <div className="flex mt-6 mb-8">
        {categoryList.map((it, i) => (
          <div
            key={it.id}
            onClick={() => {
              handleChange(it.id)
            }}
            className={classNames(styles.tabItem, {
              [styles.tabActive]: it.id === activeTab,
            })}
          >
            {it.name}
          </div>
        ))}
      </div>
    </AutoScroll>
  )
}

export default ModelResult
